import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  SmartToy as GptIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import type { PipelineResultLight } from "./types";
import { getPipelineDiagnosticCounts } from "./types";
import { colorizeEval } from "./colorizeEval";
import { NotesCell } from "./NotesCell";
import { InputItemDisplay } from "./InputItemDisplay";

interface ResultsTableProps {
  results: PipelineResultLight[];
  selectedEmailId?: string;
  onRowClick: (result: PipelineResultLight, index: number) => void;
  onOpenEmail: (emailId: string, event: React.MouseEvent) => void;
  onOpenShipment?: (shipmentId: string, event: React.MouseEvent) => void;
  onOpenReminder?: (reminderId: string, event: React.MouseEvent) => void;
  onSaveNotes?: (emailId: string, notes: string) => Promise<void>;
}

export function ResultsTable({ results, selectedEmailId, onRowClick, onOpenEmail, onOpenShipment, onOpenReminder, onSaveNotes }: ResultsTableProps) {
  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 9 * 240, "& th, & td": { width: 240 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Input</TableCell>
            <TableCell>Categories</TableCell>
            <TableCell>Properties</TableCell>
            <TableCell>Fetched Data</TableCell>
            <TableCell>Presentation</TableCell>
            <TableCell>Actions</TableCell>
            <TableCell>Eval</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((result, index) => {
            const counts = getPipelineDiagnosticCounts(result);
            return (
              <TableRow
                key={`${result.emailId || "row"}-${index}`}
                hover
                selected={result.emailId === selectedEmailId}
                sx={{
                  cursor: "pointer",
                  height: 80,
                  "& > td": { verticalAlign: "top", py: 1.5, height: 80 },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.12)",
                    },
                  },
                }}
                onClick={() => onRowClick(result, index)}
              >
                <TableCell>
                  <Box>
                    <InputItemDisplay
                      input={result.input || { emailId: result.emailId }}
                      onOpenEmail={onOpenEmail}
                      onOpenShipment={onOpenShipment}
                      onOpenReminder={onOpenReminder}
                    />
                    {result.emailSubject && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          maxWidth: 240,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                        }}
                      >
                        {result.emailSubject}
                      </Typography>
                    )}
                    {result.inbox && (
                      <Chip
                        label={result.inbox}
                        size="small"
                        variant="outlined"
                        sx={{
                          mt: 0.5,
                          maxWidth: 240,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )}
                    {result.input && (() => {
                      const extraKeys = Object.keys(result.input).filter(
                        (k) => !["emailId", "shipmentId", "reminderId", "reminderType"].includes(k) && (result.input as Record<string, unknown>)[k] != null
                      );
                      if (extraKeys.length === 0) return null;
                      return extraKeys.map((k) => (
                        <Typography key={k} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          <strong>{k}:</strong> {typeof (result.input as Record<string, unknown>)[k] === "object" ? JSON.stringify((result.input as Record<string, unknown>)[k]) : String((result.input as Record<string, unknown>)[k])}
                        </Typography>
                      ));
                    })()}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {result.categorizationStep?.categories
                      .slice(0, 3)
                      .map((cat) => (
                        <Chip key={cat} label={cat} size="small" />
                      ))}
                    {(result.categorizationStep?.categories.length || 0) > 3 && (
                      <Chip
                        label={`+${result.categorizationStep!.categories.length - 3}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {result.propertyExtractionStep?.properties?.length ? (
                    <Tooltip
                      title={
                        <Box>
                          {result.propertyExtractionStep.properties.map((p, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {p.name}: {p.value}
                            </Typography>
                          ))}
                          {(result.propertyExtractionStep.totalCount || 0) >
                            result.propertyExtractionStep.properties.length && (
                            <Typography variant="caption" color="text.secondary">
                              +
                              {(result.propertyExtractionStep.totalCount || 0) -
                                result.propertyExtractionStep.properties.length}{" "}
                              more...
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                    >
                      <Box>
                        {result.propertyExtractionStep.properties
                          .slice(0, 2)
                          .map((p, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {p.name}: {p.value}
                            </Typography>
                          ))}
                        {(result.propertyExtractionStep.totalCount || 0) > 2 && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          >
                            (+{(result.propertyExtractionStep.totalCount || 0) - 2})
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      maxWidth: 240,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                    }}
                  >
                    {result.apiFetchStep?.dataKeys?.length
                      ? result.apiFetchStep.dataKeys.join(", ")
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      maxWidth: 240,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                    }}
                  >
                    {result.presentationStep?.presentationPreview || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {result.generateActionsStep?.actionTypes?.length
                      ? result.generateActionsStep.actionTypes.map((type, idx) => (
                          <Chip key={idx} label={type} size="small" />
                        ))
                      : <Typography variant="body2" color="text.secondary">-</Typography>}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "-webkit-box",
                      maxWidth: 240,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    {colorizeEval(result.evalStep?.output)}
                  </Box>
                </TableCell>
                <TableCell>
                  {onSaveNotes ? (
                    <NotesCell
                      emailId={result.emailId}
                      initialNotes={result.notes}
                      onSaveNotes={onSaveNotes}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {result.notes || "-"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {counts.errors > 0 && (
                      <Chip
                        icon={<ErrorIcon />}
                        label={counts.errors}
                        size="small"
                        color="error"
                      />
                    )}
                    {counts.warns > 0 && (
                      <Chip
                        icon={<WarningIcon />}
                        label={counts.warns}
                        size="small"
                        color="warning"
                      />
                    )}
                    {result.gptRequestCount > 0 && (
                      <Chip
                        icon={<GptIcon />}
                        label={result.gptRequestCount}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
